import { PrivilegeEnum as a } from "@hprofits/shared/auth";
import { z as s } from "zod";
import { ocPrivate as t } from "../common/auth/procedure-builders.js";
import "../swagger/security.js";
import { SwaggerTags as e } from "../swagger/tags.js";
import { BannerSizesResponseSchema as r, AdTagsResponseSchema as o, DspsResponseSchema as i, SspsResponseSchema as n, AdUnitSitesResponseSchema as m, PaymentTypesResponseSchema as u, UserGrantsResponseSchema as p, ManagersResponseSchema as c, AdvertisersResponseSchema as d, AdUnitOwnersResponseSchema as y, BlacklistConditionParamsResponseSchema as g, BlacklistBanTypesResponseSchema as h, BlacklistStatusesResponseSchema as l, CpaAutoRulesResponseSchema as T, ContentTypeResponseSchema as R, DealTypesResponseSchema as A, LanguagesResponseSchema as I, CategoriesResponseSchema as S, BrowserTypesResponseSchema as C, DeviceTypeResponseSchema as b, AdUnitTypeQuerySchema as D, AdUnitTypeResponseSchema as O, OsQuerySchema as E, OsResponseSchema as G, CountriesQuerySchema as L, CountriesResponseSchema as N } from "./schemas.js";
const U = {
  dictionary: {
    /**
     * List all countries.
     * Accepts optional `mode` query param: 'flat' (default) returns code+name only,
     * 'full' returns countries with nested regions.
     */
    getCountries: t().route({
      method: "GET",
      path: "/dictionary/countries",
      summary: "List all countries",
      tags: [e.DICTIONARY.name]
    }).input(L).output(N).errors({}),
    getOperatingSystems: t().route({
      method: "GET",
      path: "/dictionary/oses",
      summary: "List all operating systems",
      tags: [e.DICTIONARY.name]
    }).input(E).output(G).errors({}),
    getAdUnitTypes: t().route({
      method: "GET",
      path: "/dictionary/adunit-types",
      summary: "List all ad unit types",
      tags: [e.DICTIONARY.name]
    }).input(D).output(O).errors({}),
    getDeviceTypes: t().route({
      method: "GET",
      path: "/dictionary/device-types",
      summary: "List all device types",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(b).errors({}),
    getBrowserTypes: t().route({
      method: "GET",
      path: "/dictionary/browser-types",
      summary: "List all browser types",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(C).errors({}),
    getCategories: t().route({
      method: "GET",
      path: "/dictionary/categories",
      summary: "List all categories",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(S).errors({}),
    getLanguages: t().route({
      method: "GET",
      path: "/dictionary/languages",
      summary: "List all languages",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(I).errors({}),
    getDealTypes: t().route({
      method: "GET",
      path: "/dictionary/deal-types",
      summary: "List all deal types (mapped to campaign priority)",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(A).errors({}),
    getContentTypes: t().route({
      method: "GET",
      path: "/dictionary/content-types",
      summary: "List all content-types (aka niches)",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(R).errors({}),
    getCpaAutoRules: t([a.ReadAdmin]).route({
      method: "GET",
      path: "/dictionary/cpa-auto-rules",
      summary: "List all CPA auto-rules",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(T).errors({}),
    getBlacklistStatuses: t([a.ReadAdmin]).route({
      method: "GET",
      path: "/dictionary/blacklist-statuses",
      summary: "List all blacklist ban durations",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(l).errors({}),
    getBlacklistBanTypes: t([a.ReadAdmin]).route({
      method: "GET",
      path: "/dictionary/blacklist-ban-types",
      summary: "List all blacklist ban types",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(h).errors({}),
    getBlacklistConditionParams: t([a.ReadAdmin]).route({
      method: "GET",
      path: "/dictionary/blacklist-condition-params",
      summary: "List all metrics a blacklist rule condition can compare",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(g).errors({}),
    /**
     * Distinct owners of at least one non-deleted ad unit — a facet for the ad unit list, not every
     * user in the system.
     */
    getAdUnitOwners: t([a.ReadAdmin]).route({
      method: "GET",
      path: "/dictionary/adunit-owners",
      summary: "List distinct owners of ad units",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(y).errors({}),
    /**
     * Live users with the advertiser role - the owners a goal can belong to.
     */
    getAdvertisers: t([a.ReadAdmin]).route({
      method: "GET",
      path: "/dictionary/advertisers",
      summary: "List advertisers",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(d).errors({}),
    getManagers: t([a.ReadAdmin]).route({
      method: "GET",
      path: "/dictionary/managers",
      summary: "List managers",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(c).errors({}),
    getUserGrants: t([a.ManageUsers]).route({
      method: "GET",
      path: "/dictionary/user-grants",
      summary: "List user grants",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(p).errors({}),
    getPaymentTypes: t([a.ManageUsers]).route({
      method: "GET",
      path: "/dictionary/payment-types",
      summary: "List payment types",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(u).errors({}),
    /**
     * Every live site with its owner and status — a facet for the ad unit list.
     */
    getAdUnitSites: t([a.ReadAdmin]).route({
      method: "GET",
      path: "/dictionary/adunit-sites",
      summary: "List live sites with their owner and status",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(m).errors({}),
    /**
     * Live SSPs — options for the per-SSP traffic source filter (`traffic_source_id` in statistics).
     */
    getSsps: t([a.SplitTrafficSourceBySsp]).route({
      method: "GET",
      path: "/dictionary/ssps",
      summary: "List live SSPs",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(n).errors({}),
    getDsps: t([a.ManageAdExchangeBindings]).route({
      method: "GET",
      path: "/dictionary/dsps",
      summary: "List live DSPs",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(i).errors({}),
    /**
     * Ad compliance tags for landings and creatives; open to any authenticated user because
     * advertisers pick them in the landing editor.
     */
    getAdTags: t().route({
      method: "GET",
      path: "/dictionary/ad-tags",
      summary: "List all ad compliance tags",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(o).errors({}),
    /**
     * Banner sizes for a banner creative without a file; open to any authenticated user because
     * advertisers pick one in the creative editor.
     */
    getBannerSizes: t().route({
      method: "GET",
      path: "/dictionary/banner-sizes",
      summary: "List the sizes of a custom-content banner creative",
      tags: [e.DICTIONARY.name]
    }).input(s.object({})).output(r).errors({})
  }
};
export {
  U as dictionaryContract
};
//# sourceMappingURL=contract.js.map
